import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Table,
  Badge,
  Spinner,
  Alert,
  Form,
  Row,
  Col,
  Card,
  ButtonGroup,
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaEnvelope,
  FaEye,
  FaRedo,
  FaArrowLeft,
  FaInfoCircle,
} from "react-icons/fa";

import {
  getSchoolMetricEvents,
  getSchoolMetricEventById,
  getSchoolMetricEventMessages,
  getSchoolMetricEventsWithErrors,
  getUnprocessedSchoolMetricEvents,
  reprocessSchoolMetricEvent,
  getIndividualSchool,
  getTeacherById,
} from "../services/APIClient";

// Constants
const CONSTANTS = {
  DEFAULT_LIMIT: 100,
  MAX_TABLE_HEIGHT: "500px",
  FILTER_OPTIONS: [
    { value: "all", label: "All Events" },
    { value: "unprocessed", label: "Unprocessed" },
    { value: "errors", label: "With Errors" },
  ],
  PRIORITY_COLORS: {
    urgent: "danger",
    high: "warning",
    medium: "info",
    low: "secondary",
  },
};

function EventLogModal({ show, onHide }) {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showConfirmReprocess, setShowConfirmReprocess] = useState(false);
  const [eventToReprocess, setEventToReprocess] = useState(null);

  // Cache for schools and teachers to avoid duplicate API calls
  const [schoolCache, setSchoolCache] = useState({});
  const [teacherCache, setTeacherCache] = useState({});

  useEffect(() => {
    if (show) {
      loadEvents();
      // Reset details when modal opens
      setSelectedEvent(null);
      setEventDetails(null);
      setError(null);
      setSuccess(null);
      setSchoolCache({});
      setTeacherCache({});
    }
  }, [show]);

  useEffect(() => {
    if (show && !selectedEvent) {
      loadEvents();
    }
  }, [filter]);

  // Helper function to fetch school name
  const fetchSchoolName = async (schoolId) => {
    if (!schoolId) return null;
    
    // Check cache first
    if (schoolCache[schoolId]) {
      return schoolCache[schoolId];
    }

    try {
      const school = await getIndividualSchool(schoolId);
      const schoolName = school.name || schoolId;
      setSchoolCache(prev => ({ ...prev, [schoolId]: schoolName }));
      return schoolName;
    } catch (err) {
      console.error(`Failed to fetch school ${schoolId}:`, err);
      return schoolId; // Fallback to ID
    }
  };

  // Helper function to fetch teacher data
  const fetchTeacherData = async (teacherId) => {
    if (!teacherId) return null;
    
    // Check cache first
    if (teacherCache[teacherId]) {
      return teacherCache[teacherId];
    }

    try {
      const teacher = await getTeacherById(teacherId);
      const teacherData = {
        name: teacher.name || teacherId,
        email: teacher.email || null,
      };
      setTeacherCache(prev => ({ ...prev, [teacherId]: teacherData }));
      return teacherData;
    } catch (err) {
      console.error(`Failed to fetch teacher ${teacherId}:`, err);
      return { name: teacherId, email: null }; // Fallback to ID
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (filter === "errors") {
        data = await getSchoolMetricEventsWithErrors();
      } else if (filter === "unprocessed") {
        data = await getUnprocessedSchoolMetricEvents();
      } else {
        data = await getSchoolMetricEvents({ filter: { limit: CONSTANTS.DEFAULT_LIMIT } });
      }
      
      const eventsArray = Array.isArray(data) ? data : [];
      
      // Fetch school names for all events
      const eventsWithSchools = await Promise.all(
        eventsArray.map(async (event) => ({
          ...event,
          schoolName: await fetchSchoolName(event.schoolId),
        }))
      );
      
      setEvents(eventsWithSchools);
    } catch (err) {
      console.error("Failed to load events:", err);
      setError("Failed to load events. Please try again.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (event) => {
    setSelectedEvent(event);
    setDetailsLoading(true);
    setError(null);

    try {
      const [details, messages] = await Promise.all([
        getSchoolMetricEventById(event.id),
        getSchoolMetricEventMessages(event.id).catch((err) => {
          console.error("Error fetching event messages:", err);
          return [];
        }),
      ]);

      // Fetch school name if not already cached
      const schoolName = await fetchSchoolName(details.schoolId);

      // Fetch teacher data for all messages
      const messagesArray = Array.isArray(messages) ? messages : [];
      const messagesWithTeachers = await Promise.all(
        messagesArray.map(async (msg) => {
          const teacherData = await fetchTeacherData(msg.teacherId);
          return {
            ...msg,
            teacher: teacherData,
          };
        })
      );

      setEventDetails({
        ...details,
        schoolName,
        messages: messagesWithTeachers,
      });
    } catch (err) {
      console.error("Failed to load event details:", err);
      setError("Failed to load event details. Please try again.");
      setEventDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleReprocessClick = (event) => {
    setEventToReprocess(event);
    setShowConfirmReprocess(true);
  };

  const handleReprocessConfirm = async () => {
    if (!eventToReprocess) return;

    setReprocessing(true);
    setError(null);
    setSuccess(null);
    setShowConfirmReprocess(false);

    try {
      const result = await reprocessSchoolMetricEvent(eventToReprocess.id);
      
      if (result.success) {
        setSuccess("Event reprocessed successfully. Notifications have been resent.");
        await loadEvents(); // Refresh list
        
        // If we're viewing the reprocessed event, refresh its details
        if (selectedEvent?.id === eventToReprocess.id) {
          await handleViewDetails(selectedEvent);
        }
      } else {
        setError(result.message || "Failed to reprocess event");
      }
    } catch (err) {
      console.error("Failed to reprocess event:", err);
      setError("Failed to reprocess event: " + (err.message || "Unknown error"));
    } finally {
      setReprocessing(false);
      setEventToReprocess(null);
    }
  };

  const handleBack = () => {
    setSelectedEvent(null);
    setEventDetails(null);
    setSuccess(null);
    setError(null);
  };

  const handleModalClose = () => {
    handleBack();
    setFilter("all");
    setError(null);
    setSuccess(null);
    onHide();
  };

  const getStatusBadge = (event) => {
    if (event.processed) {
      if (event.processingError) {
        return (
          <Badge bg="danger">
            <FaExclamationTriangle style={{ marginRight: "0.3rem" }} />
            Error
          </Badge>
        );
      }
      return (
        <Badge bg="success">
          <FaCheckCircle style={{ marginRight: "0.3rem" }} />
          Processed
        </Badge>
      );
    }
    return <Badge bg="warning">Pending</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const bg = CONSTANTS.PRIORITY_COLORS[priority] || "secondary";
    return <Badge bg={bg}>{priority || "medium"}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid date";
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleTimeString();
    } catch {
      return "Invalid time";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "Invalid date/time";
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleModalClose} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedEvent
              ? `Event Details - ${selectedEvent.id}`
              : "Event Log"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <FaExclamationTriangle style={{ marginRight: "0.5rem" }} />
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setSuccess(null)}
            >
              <FaCheckCircle style={{ marginRight: "0.5rem" }} />
              {success}
            </Alert>
          )}

          {!selectedEvent ? (
            // List View
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0">Triggered Events ({events.length})</h5>
                  <small className="text-muted">
                    Schools that reached thresholds and triggered notifications
                  </small>
                </div>
                <Form.Select
                  style={{ width: "200px" }}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  disabled={loading}
                >
                  {CONSTANTS.FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="text-muted mt-2">Loading events...</p>
                </div>
              ) : events.length === 0 ? (
                <Alert variant="info">
                  <FaInfoCircle style={{ marginRight: "0.5rem" }} />
                  No events found.{" "}
                  {filter !== "all"
                    ? "Try changing the filter."
                    : "Events are created when schools meet rule thresholds."}
                </Alert>
              ) : (
                <div
                  style={{
                    maxHeight: CONSTANTS.MAX_TABLE_HEIGHT,
                    overflowY: "auto",
                  }}
                >
                  <Table striped bordered hover responsive>
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "white",
                        zIndex: 1,
                      }}
                    >
                      <tr>
                        <th>Status</th>
                        <th>School</th>
                        <th>Event Type</th>
                        <th>Metric Value</th>
                        <th>Academic Year</th>
                        <th>Triggered</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <tr key={event.id}>
                          <td>{getStatusBadge(event)}</td>
                          <td>
                            <strong>{event.schoolName || event.schoolId}</strong>
                          </td>
                          <td>{event.eventType || "N/A"}</td>
                          <td>
                            <strong>
                              {event.metricValue !== undefined &&
                              event.metricValue !== null
                                ? `${event.metricValue}%`
                                : "N/A"}
                            </strong>
                          </td>
                          <td>{event.academicYear || "N/A"}</td>
                          <td>
                            <div>{formatDate(event.triggeredAt)}</div>
                            <small className="text-muted">
                              {formatTime(event.triggeredAt)}
                            </small>
                          </td>
                          <td>
                            <ButtonGroup size="sm">
                              <Button
                                variant="outline-primary"
                                onClick={() => handleViewDetails(event)}
                                title="View details"
                                disabled={reprocessing}
                              >
                                <FaEye />
                              </Button>
                              {(event.processingError || !event.processed) && (
                                <Button
                                  variant="outline-warning"
                                  onClick={() => handleReprocessClick(event)}
                                  title="Reprocess event"
                                  disabled={reprocessing}
                                >
                                  <FaRedo />
                                </Button>
                              )}
                            </ButtonGroup>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          ) : (
            // Details View
            <>
              {detailsLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <p className="text-muted mt-2">Loading details...</p>
                </div>
              ) : eventDetails ? (
                <>
                  <Row className="mb-4">
                    <Col md={6}>
                      <Card>
                        <Card.Body>
                          <h6 className="mb-3">Event Information</h6>
                          <table className="table table-sm mb-0">
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Status:</strong>
                                </td>
                                <td>{getStatusBadge(eventDetails)}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>School:</strong>
                                </td>
                                <td>
                                  {eventDetails.schoolName || eventDetails.schoolId || "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Event Type:</strong>
                                </td>
                                <td>{eventDetails.eventType || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Metric Value:</strong>
                                </td>
                                <td>
                                  <strong>
                                    {eventDetails.metricValue !== undefined &&
                                    eventDetails.metricValue !== null
                                      ? `${eventDetails.metricValue}%`
                                      : "N/A"}
                                  </strong>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Academic Year:</strong>
                                </td>
                                <td>{eventDetails.academicYear || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Triggered:</strong>
                                </td>
                                <td>
                                  {formatDateTime(eventDetails.triggeredAt)}
                                </td>
                              </tr>
                              {eventDetails.processedAt && (
                                <tr>
                                  <td>
                                    <strong>Processed:</strong>
                                  </td>
                                  <td>
                                    {formatDateTime(eventDetails.processedAt)}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card>
                        <Card.Body>
                          <h6 className="mb-3">Rule Information</h6>
                          <table className="table table-sm mb-0">
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Rule ID:</strong>
                                </td>
                                <td>
                                  <code>{eventDetails.ruleId || "N/A"}</code>
                                </td>
                              </tr>
                              {eventDetails.jobRunId && (
                                <tr>
                                  <td>
                                    <strong>Job Run ID:</strong>
                                  </td>
                                  <td>
                                    <code>{eventDetails.jobRunId}</code>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Error Section */}
                  {eventDetails.processingError && (
                    <Card className="mb-3">
                      <Card.Header className="bg-danger text-white">
                        <FaExclamationTriangle
                          style={{ marginRight: "0.5rem" }}
                        />
                        Processing Error
                      </Card.Header>
                      <Card.Body>
                        <p className="text-danger mb-3">
                          {eventDetails.processingError}
                        </p>
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleReprocessClick(eventDetails)}
                          disabled={reprocessing}
                        >
                          {reprocessing ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                style={{ marginRight: "0.5rem" }}
                              />
                              Reprocessing...
                            </>
                          ) : (
                            <>
                              <FaRedo style={{ marginRight: "0.5rem" }} />
                              Retry Processing
                            </>
                          )}
                        </Button>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Messages Section */}
                  {eventDetails.messages && eventDetails.messages.length > 0 ? (
                    <Card>
                      <Card.Header>
                        <FaEnvelope style={{ marginRight: "0.5rem" }} />
                        Messages Sent ({eventDetails.messages.length})
                      </Card.Header>
                      <Card.Body>
                        <div
                          style={{
                            maxHeight: "300px",
                            overflowY: "auto",
                          }}
                        >
                          <Table striped bordered hover size="sm">
                            <thead>
                              <tr>
                                <th>Recipient</th>
                                <th>Type</th>
                                <th>Title</th>
                                <th>Priority</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {eventDetails.messages.map((msg) => (
                                <tr key={msg.id}>
                                  <td>
                                    {msg.teacher?.name || msg.teacherId || "N/A"}
                                    {msg.teacher?.email && (
                                      <div className="text-muted small">
                                        {msg.teacher.email}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <Badge bg="info">
                                      {msg.messageType || "notification"}
                                    </Badge>
                                  </td>
                                  <td>{msg.title || "N/A"}</td>
                                  <td>{getPriorityBadge(msg.priority)}</td>
                                  <td>
                                    {msg.read ? (
                                      <Badge bg="success">Read</Badge>
                                    ) : msg.dismissed ? (
                                      <Badge bg="secondary">Dismissed</Badge>
                                    ) : (
                                      <Badge bg="warning">Unread</Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Card.Body>
                    </Card>
                  ) : (
                    <Alert variant="info">
                      <FaInfoCircle style={{ marginRight: "0.5rem" }} />
                      No messages were sent for this event.
                    </Alert>
                  )}

                  <div className="mt-3">
                    <Button variant="secondary" onClick={handleBack}>
                      <FaArrowLeft style={{ marginRight: "0.5rem" }} />
                      Back to List
                    </Button>
                  </div>
                </>
              ) : (
                <Alert variant="warning">
                  <FaExclamationTriangle style={{ marginRight: "0.5rem" }} />
                  Failed to load event details. Please try again.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {!selectedEvent && (
            <Button variant="secondary" onClick={handleModalClose}>
              Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Reprocess Confirmation Modal */}
      <Modal
        show={showConfirmReprocess}
        onHide={() => setShowConfirmReprocess(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Reprocess Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to reprocess this event? This will resend
            notifications to the relevant recipients.
          </p>
          {eventToReprocess && (
            <Card className="bg-light">
              <Card.Body>
                <small>
                  <strong>Event:</strong> {eventToReprocess.eventType}
                  <br />
                  <strong>School:</strong>{" "}
                  {eventToReprocess.schoolName || eventToReprocess.schoolId}
                  <br />
                  <strong>Metric:</strong> {eventToReprocess.metricValue}%
                </small>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmReprocess(false)}
            disabled={reprocessing}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleReprocessConfirm}
            disabled={reprocessing}
          >
            {reprocessing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  style={{ marginRight: "0.5rem" }}
                />
                Reprocessing...
              </>
            ) : (
              <>
                <FaRedo style={{ marginRight: "0.5rem" }} />
                Confirm Reprocess
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

EventLogModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default EventLogModal;